import React, { useState, useEffect } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  CssBaseline,
  Divider,
  Avatar,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
  Tooltip,
  Badge,
  Chip,
  Collapse,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  InputAdornment,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  CalendarToday as CalendarIcon,
  TrendingUp as TrendingUpIcon,
  Assignment as AssignmentIcon,
  Payment as PaymentIcon,
  Category as CategoryIcon,
  Group as GroupIcon,
  Assessment as AssessmentIcon,
  Notifications as NotificationsIcon,
  AccountCircle as AccountCircleIcon,
  ExpandLess,
  ExpandMore,
  Person as PersonIcon,
  ExitToApp as ExitToAppIcon,
  PhotoCamera as PhotoCameraIcon,
  Search as SearchIcon,
  Cancel as CancelIcon,
  Save as SaveIcon,
  Settings as SettingsIcon,
  ChevronLeft as ChevronLeftIcon,
  CameraAlt as CameraAltIcon,
  PhotoLibrary as PhotoLibraryIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  CloudUpload as CloudUploadIcon,
  SupportAgent as SupportAgentIcon
} from '@mui/icons-material';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { notificacaoService, usuarioService } from '../../services/api';
// =====================================================
// TIPOS E INTERFACES
// =====================================================

interface MenuItem {
  title: string;
  icon: React.ReactNode;
  path?: string;
  badge?: number;
  children?: MenuItem[];
  permission?: string; // Campo para mapear a permissão necessária
}

interface LayoutProps {
  children?: React.ReactNode;
}

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://trajetus.net';

const resolveImageSrc = (value?: string | null): string | undefined => {
  if (!value || typeof value !== 'string') return undefined;
  const v = value.trim();
  if (!v) return undefined;
  if (v.startsWith('data:')) {
    const idx = v.indexOf(';base64,');
    if (idx >= 0) {
      const payload = v.substring(idx + ';base64,'.length).trim();
      if (payload.startsWith('http') || payload.startsWith('https//') || payload.startsWith('http//')) {
        let url = payload;
        if (url.startsWith('https//')) url = `https://${url.substring('https//'.length)}`;
        if (url.startsWith('http//')) url = `http://${url.substring('http//'.length)}`;
        url = url.replace('ppswhatsappnet', 'pps.whatsapp.net').replace('whatsappnet', 'whatsapp.net');
        return url;
      }
    }
    return v;
  }
  if (v.startsWith('blob:')) return v;
  if (v.startsWith('http://') || v.startsWith('https://')) return v;
  if (v.startsWith('https//')) return `https://${v.substring('https//'.length)}`;
  if (v.startsWith('http//')) return `http://${v.substring('http//'.length)}`;
  if (v.startsWith('/')) {
    const base = (API_BASE_URL || '').replace(/\/$/, '');
    if (v.startsWith('/uploads/') || v.includes('/uploads/')) {
      return `${base}${v}`;
    }
    return v;
  }
  if (v.includes('/')) {
    const base = (API_BASE_URL || '').replace(/\/$/, '');
    return `${base}/${v.replace(/^\//, '')}`;
  }
  return `data:image/jpeg;base64,${v}`;
};

// =====================================================
// CONFIGURAÇÃO DO MENU
// =====================================================

const menuItems: MenuItem[] = [
  { title: 'Dashboard', icon: <DashboardIcon />, path: '/app/dashboard', permission: 'menu_dashboard' },
  { title: 'Clientes', icon: <PeopleIcon />, path: '/app/clientes', permission: 'menu_clientes' },
  { title: 'Agendamento', icon: <CalendarIcon />, path: '/app/agendamento-calendario', permission: 'menu_agendamento' },
  { title: 'Evoluções', icon: <TrendingUpIcon />, path: '/app/evolucoes', permission: 'menu_evolucoes' },
  { title: 'Experiências', icon: <AssignmentIcon />, path: '/app/experiencias', permission: 'menu_experiencia' },
  { 
    title: 'Financeiro', 
    icon: <PaymentIcon />, 
    permission: 'menu_financeiro',
    children: [
      { title: 'Recebimentos', icon: <PaymentIcon />, path: '/app/recebimentos', permission: 'menu_recebimentos' },
      { title: 'Despesas', icon: <PaymentIcon />, path: '/app/despesas', permission: 'menu_despesas' },
      { title: 'Tipos de Despesa', icon: <CategoryIcon />, path: '/app/tipos-despesa', permission: 'menu_tipos_despesa' },
      { title: 'RaioX Financeiro', icon: <AssessmentIcon />, path: '/app/raiox-financeiro', permission: 'menu_raiox_financeiro' }
    ]
  },
  { 
    title: 'Utilidades', 
    icon: <AssignmentIcon />, 
    permission: 'menu_utilidades',
    children: [
      { title: 'Gerar Mensalidades', icon: <PaymentIcon />, path: '/app/gerar-mensalidades', permission: 'menu_gerar_mensalidades' },
      { title: 'Gerar Agenda', icon: <CalendarIcon />, path: '/app/gerar-agenda', permission: 'menu_gerar_agenda' }
    ]
  },
  { 
    title: 'Configurações', 
    icon: <SettingsIcon />, 
    permission: 'menu_configuracoes',
    children: [
      { title: 'Usuários', icon: <GroupIcon />, path: '/app/usuarios', permission: 'menu_usuarios' },
      { title: 'Níveis de Acesso', icon: <GroupIcon />, path: '/app/niveis-acesso', permission: 'menu_niveis_acesso' },
      { title: 'Notificações', icon: <NotificationsIcon />, path: '/app/configuracoes', permission: 'menu_notificacoes' },
      { title: 'Logs do Sistema', icon: <AssessmentIcon />, path: '/app/logs', permission: 'menu_logs' }
    ]
  },
  { title: 'Suporte Técnico', icon: <SupportAgentIcon />, path: '/app/suporte-tecnico' }
];

// =====================================================
// COMPONENTE PRINCIPAL
// =====================================================

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, updateUser, userPermissions, clearStorageAndReloadPermissions } = useAuth();
  
  // Log temporário para debug
  console.log('🔍 [DEBUG Layout] Dados do usuário:', user);
  console.log('🔍 [DEBUG Layout] Nome:', user?.user_name);
  console.log('🔍 [DEBUG Layout] User_name:', user?.user_name);
  
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState<boolean>(!isMobile);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [expandedItems, setExpandedItems] = useState<{[key: string]: boolean}>({});
  const [notificacoesNaoLidas, setNotificacoesNaoLidas] = useState<number>(0);
  const [notificacoes, setNotificacoes] = useState<any[]>([]);
  const [notificacoesAnchorEl, setNotificacoesAnchorEl] = useState<null | HTMLElement>(null);
  const notificacoesOpen = Boolean(notificacoesAnchorEl);

  // Estados para o modal de perfil
  const [perfilModalOpen, setPerfilModalOpen] = useState(false);
  const [perfilError, setPerfilError] = useState('');
  const [perfilSuccess, setPerfilSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [perfilFieldErrors, setPerfilFieldErrors] = useState({
    novaSenha: false
  });
  const [perfilData, setPerfilData] = useState({
    foto: '',
    assinatura: '',
    nome: '',
    novaSenha: '',
    dataNascimento: '',
    celular: '',
    email: '',
    crp: '',
    cpf: '',
    rg: '',
    cep: '',
    endereco: '',
    numero: '',
    bairro: '',
    cidade: '',
    uf: ''
  });

  // Carregar dados do usuário no modal
  useEffect(() => {
    const carregarDadosCompletos = async () => {
      if (perfilModalOpen && user) {
        try {
          // Buscar dados completos do usuário via usuarioService
          const response = await usuarioService.buscarPorId(user.id);
          const userData = response.data?.data || response.data || response;
          setPerfilData({
            foto: userData.foto || '',
            assinatura: userData.assinatura || '',
            nome: userData.user_name || user?.user_name || '',
            novaSenha: '',
            dataNascimento: userData.data_nascimento ? userData.data_nascimento.split(' ')[0] : '',
            celular: userData.celular || '',
            email: userData.email || user?.email || '',
            crp: userData.crp || '',
            cpf: userData.cpf || '',
            rg: userData.rg || '',
            cep: userData.cep || '',
            endereco: userData.endereco || '',
            numero: userData.numero || '',
            bairro: userData.bairro || '',
            cidade: userData.cidade || '',
            uf: userData.uf || ''
          });
        } catch (error: any) {
          // Fallback para dados do contexto
          setPerfilData({
            foto: user?.foto || '',
            assinatura: user?.assinatura || '',
            nome: user?.user_name || '',
            novaSenha: '',
            dataNascimento: user?.data_nascimento ? user.data_nascimento.split(' ')[0] : '',
            celular: user?.celular || '',
            email: user?.email || '',
            crp: user?.crp || '',
            cpf: user?.cpf || '',
            rg: user?.rg || '',
            cep: user?.cep || '',
            endereco: user?.endereco || '',
            numero: user?.numero || '',
            bairro: user?.bairro || '',
            cidade: user?.cidade || '',
            uf: user?.uf || ''
          });
        }
      }
    };
    
    carregarDadosCompletos();
  }, [perfilModalOpen, user]);

  // Funções de formatação
  const formatCelular = (value: string): string => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 11) {
      return numbers.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    }
    return value;
  };

  const formatCPF = (value: string): string => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 11) {
      return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    }
    return value;
  };

  const formatRG = (value: string): string => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 9) {
      return numbers.replace(/(\d{2})(\d{3})(\d{3})(\d{1})/, '$1.$2.$3-$4');
    }
    return value;
  };

  // Funções de validação
  const validateCelular = (celular: string): boolean => {
    if (!celular) return true;
    const numbers = celular.replace(/\D/g, '');
    return numbers.length === 11;
  };

  const validateSenha = (senha: string): boolean => {
    if (!senha) return true; // Vazio é válido para edição
    
    // Mínimo 8 caracteres
    if (senha.length < 8) return false;
    
    // Ao menos uma letra
    if (!/[a-zA-Z]/.test(senha)) return false;
    
    // Ao menos um número
    if (!/[0-9]/.test(senha)) return false;
    
    // Ao menos um caracter especial
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(senha)) return false;
    
    return true;
  };

  const handlePerfilInputChange = (field: string, value: string) => {
    let formattedValue = value;
    
    switch (field) {
      case 'celular':
        formattedValue = formatCelular(value);
        break;
      case 'cpf':
        formattedValue = formatCPF(value);
        break;
      case 'rg':
        formattedValue = formatRG(value);
        break;
    }
    
    setPerfilData(prev => ({ ...prev, [field]: formattedValue }));
    
    // Validação para senha
    if (field === 'novaSenha') {
      const isValid = validateSenha(value);
      setPerfilFieldErrors(prev => ({ ...prev, novaSenha: !isValid }));
    }
  };

  const fecharModalPerfil = () => {
    setPerfilModalOpen(false);
    setPerfilError('');
    setPerfilSuccess('');
  };

  const handleSalvarPerfil = async () => {
    try {
      setPerfilError('');
      setPerfilSuccess('');

      // Preparar dados para envio
      const dadosParaEnvio: any = {
        user_name: perfilData.nome,
        email: perfilData.email,
        celular: perfilData.celular,
        data_nascimento: perfilData.dataNascimento,
        crp: perfilData.crp,
        cpf: perfilData.cpf,
        rg: perfilData.rg,
        cep: perfilData.cep,
        endereco: perfilData.endereco,
        numero: perfilData.numero,
        bairro: perfilData.bairro,
        cidade: perfilData.cidade,
        uf: perfilData.uf
      };

      // Adicionar foto apenas se não estiver vazia
      if (perfilData.foto && typeof perfilData.foto === 'string' && perfilData.foto.trim() !== '') {
        dadosParaEnvio.foto = perfilData.foto;
      }

      // Adicionar assinatura apenas se não estiver vazia
      if (perfilData.assinatura && typeof perfilData.assinatura === 'string' && perfilData.assinatura.trim() !== '') {
        dadosParaEnvio.assinatura = perfilData.assinatura;
      }

      // Adicionar senha apenas se foi fornecida
      if (perfilData.novaSenha && perfilData.novaSenha.trim() !== '') {
        dadosParaEnvio.senha = perfilData.novaSenha;
      }

      const response = await usuarioService.atualizar(user?.id, dadosParaEnvio);
      
      // Atualizar contexto do usuário com novos dados
      updateUser({
        nome: perfilData.nome,
        user_name: perfilData.nome,
        email: perfilData.email,
        foto: perfilData.foto,
        celular: perfilData.celular
      });
      
      setPerfilSuccess('Perfil atualizado com sucesso!');
      
      // Fechar modal após 2 segundos
      setTimeout(() => {
        setPerfilModalOpen(false);
        setPerfilSuccess('');
      }, 2000);
      
    } catch (error: any) {
      console.error('Erro ao salvar perfil:', error);
      setPerfilError(error.response?.data?.message || error.message || 'Erro ao salvar perfil');
    }
  };

  const buscarEnderecoPorCEP = async () => {
    try {
      const cep = perfilData.cep.replace(/\D/g, '');
      if (cep.length !== 8) {
        setPerfilError('CEP inválido. Por favor, digite um CEP válido.');
        return;
      }
      
      const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const dados = await response.json();
      
      if (dados.erro) {
        setPerfilError('CEP não encontrado. Por favor, verifique o CEP digitado.');
        return;
      }
      
      setPerfilData(prev => ({
        ...prev,
        endereco: dados.logradouro,
        bairro: dados.bairro,
        cidade: dados.localidade,
        uf: dados.uf
      }));
      
    } catch (error) {
      console.error('Erro ao buscar endereço por CEP:', error);
      setPerfilError('Erro ao buscar endereço por CEP. Tente novamente.');
    }
  };

  const handleUploadFoto = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      if (event.target.files && event.target.files.length > 0) {
        const file = event.target.files[0];
        const reader = new FileReader();
        
        reader.onload = (e) => {
          const foto = e.target?.result as string;
          setPerfilData(prev => ({ ...prev, foto }));
        };
        
        reader.readAsDataURL(file);
      }
    } catch (error) {
      console.error('Erro ao carregar foto:', error);
      setPerfilError('Erro ao carregar foto. Tente novamente.');
    }
  };

  const handleUploadAssinatura = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      if (event.target.files && event.target.files.length > 0) {
        const file = event.target.files[0];
        const reader = new FileReader();
        
        reader.onload = (e) => {
          const assinatura = e.target?.result as string;
          setPerfilData(prev => ({ ...prev, assinatura }));
        };
        
        reader.readAsDataURL(file);
      }
    } catch (error) {
      console.error('Erro ao carregar assinatura:', error);
      setPerfilError('Erro ao carregar assinatura. Tente novamente.');
    }
  };

  // =====================================================
  // ESTADOS E EFEITOS
  // =====================================================
  useEffect(() => {
    if (isMobile) {
      setDrawerOpen(false);
    } else {
      setDrawerOpen(true);
    }
  }, [isMobile]);

  useEffect(() => {
    const carregarNotificacoes = async () => {
      try {
        if (user?.id) {
          const response = await notificacaoService.contarNaoLidas(user.id);
          setNotificacoesNaoLidas(response.data?.total || 0);
        }
      } catch (error) {
        console.error('Erro ao carregar notificações não lidas:', error);
        setNotificacoesNaoLidas(0);
      }
    };
    
    carregarNotificacoes();
    
    // Remover intervalo automático para evitar rate limiting
    // const interval = setInterval(carregarNotificacoes, 30000);
    // return () => clearInterval(interval);
  }, [user?.id]);

  const carregarNotificacoesCompletas = async () => {
    try {
      if (user?.id) {
        const response = await notificacaoService.listarPorUsuario(user.id);
        const todasNotificacoes = response.data?.data || response.data || [];
        
        // Filtrar notificações não lidas (lido pode ser 0, false, null)
        const notificacoesNaoLidas = todasNotificacoes.filter((n: any) => {
          const isLida = n.lido === 1 || n.lido === true || n.lido === '1';
          return !isLida;
        });
        
        setNotificacoes(notificacoesNaoLidas);
      }
    } catch (error) {
      console.error('Erro ao carregar notificações completas:', error);
      setNotificacoes([]);
    }
  };

  const handleNotificacoesClick = async (event: React.MouseEvent<HTMLElement>) => {
    setNotificacoesAnchorEl(event.currentTarget);
    await carregarNotificacoesCompletas();
  };

  const marcarComoLida = async (id: number) => {
    try {
      await notificacaoService.marcarComoLida(id);
      
      // Aguardar um pouco antes de recarregar para evitar rate limiting
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Atualizar apenas a lista local removendo a notificação marcada
      setNotificacoes(prev => prev.filter(n => n.id !== id));
      
      // Atualizar contagem decrementando
      setNotificacoesNaoLidas(prev => Math.max(0, prev - 1));
      
    } catch (error) {
      console.error('Erro ao marcar notificação como lida:', error);
    }
  };

  const marcarTodasComoLidas = async () => {
    try {
      // Marcar cada notificação não lida como lida com delay maior
      for (const notificacao of notificacoes) {
        await notificacaoService.marcarComoLida(notificacao.id);
        // Delay maior para evitar rate limiting
        await new Promise(resolve => setTimeout(resolve, 1500));
      }
      
      // Limpar lista local
      setNotificacoes([]);
      
      // Zerar contagem
      setNotificacoesNaoLidas(0);
      
      // Fechar o popup
      setNotificacoesAnchorEl(null);
      
    } catch (error) {
      console.error('Erro ao marcar todas as notificações como lidas:', error);
    }
  };

  // =====================================================
  // FUNÇÕES DE CONTROLE
  // =====================================================
  const handleDrawerToggle = (): void => {
    setDrawerOpen(!drawerOpen);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = (): void => {
    setAnchorEl(null);
  };

  const handleLogout = (): void => {
    handleMenuClose();
    logout();
    navigate('/login', { replace: true });
  };

  const handleNavigation = (path: string): void => {
    navigate(path);
    if (isMobile) {
      setDrawerOpen(false);
    }
  };

  const handleExpandClick = (title: string): void => {
    setExpandedItems(prev => {
      // Se o item já está expandido, apenas recolhe ele
      if (prev[title]) {
        return { [title]: false };
      }
      // Se não está expandido, expande ele e recolhe todos os outros
      return { [title]: true };
    });
  };

  const isActiveRoute = (path: string): boolean => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // Função para verificar se o usuário tem permissão para um item do menu
  const hasPermission = (permission?: string): boolean => {
    if (!permission) return true; // Se não há permissão definida, mostrar sempre
    
    // Se não há permissões carregadas ainda, permitir acesso temporariamente
    if (!userPermissions) {
      console.log('Permissões ainda não carregadas, permitindo acesso temporário');
      return true;
    }
    
    // Verificar se as permissões estão em um array ou objeto aninhado
    let permissionValue;
    
    // Caso 1: Permissões diretas no objeto
    if (userPermissions[permission] !== undefined) {
      permissionValue = userPermissions[permission];
    }
    // Caso 2: Permissões em um array de objetos
    else if (Array.isArray(userPermissions)) {
      const permObj = userPermissions.find(p => p.permission === permission || p.nome === permission);
      permissionValue = permObj ? (permObj.ativo || permObj.value || permObj.enabled) : false;
    }
    // Caso 3: Permissões em um objeto aninhado
    else if (userPermissions.permissions) {
      permissionValue = userPermissions.permissions[permission];
    }
    // Caso 4: Permissões em data
    else if (userPermissions.data) {
      permissionValue = userPermissions.data[permission];
    }
    
    // Converter para boolean
    let hasAccess = false;
    if (typeof permissionValue === 'boolean') {
      hasAccess = permissionValue;
    } else if (typeof permissionValue === 'number') {
      hasAccess = permissionValue === 1;
    } else if (typeof permissionValue === 'string') {
      hasAccess = permissionValue === '1' || permissionValue === 'true';
    }
    
    return hasAccess;
  };

  // Função para filtrar itens do menu baseado nas permissões
  const filterMenuItems = (items: MenuItem[]): MenuItem[] => {
    return items.filter(item => {
      // Verificar se o usuário tem permissão para o item principal
      if (!hasPermission(item.permission)) {
        return false;
      }

      // Se o item tem filhos, filtrar os filhos também
      if (item.children) {
        const filteredChildren = item.children.filter(child => hasPermission(child.permission));
        // Se não restaram filhos após o filtro, ocultar o item pai
        if (filteredChildren.length === 0) {
          return false;
        }
        // Atualizar o item com os filhos filtrados
        item.children = filteredChildren;
      }

      return true;
    });
  };

  // Obter itens do menu filtrados pelas permissões do usuário
  const filteredMenuItems = filterMenuItems([...menuItems]);

  // =====================================================
  // RENDERIZAÇÃO DO DRAWER
  // =====================================================
  const drawer = (
    <Box>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        p: 2,
        minHeight: 64, // Mesma altura do AppBar/Toolbar
        borderBottom: `1px solid ${theme.palette.divider}`
      }}>
        <Box 
          component="img" 
          src="/logo_header.png" 
          alt="Trajetus" 
          sx={{ 
            height: 40,
            width: 'auto',
            margin: '0 16px 0 16px'
          }} 
        />
        
        {isMobile && (
          <IconButton onClick={handleDrawerToggle}>
            <ChevronLeftIcon />
          </IconButton>
        )}
      </Box>
      
      <Divider />
      
      <List sx={{ pt: 1 }}>
        {filteredMenuItems.map((item) => (
          <React.Fragment key={item.title}>
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => {
                  if (item.children) {
                    handleExpandClick(item.title);
                  } else if (item.path) {
                    handleNavigation(item.path);
                  }
                }}
                selected={item.path ? isActiveRoute(item.path) : false}
                sx={{
                  '&.Mui-selected': {
                    backgroundColor: theme.palette.primary.light,
                    color: 'white',
                    '& .MuiListItemIcon-root': {
                      color: 'white',
                    },
                    '& .MuiListItemText-primary': {
                      color: 'white',
                    },
                    '&:hover': {
                      backgroundColor: theme.palette.primary.light,
                    },
                  },
                  backgroundColor: expandedItems[item.title] ? `${theme.palette.primary.main}08` : 'transparent',
                  border: expandedItems[item.title] ? `1px solid ${theme.palette.primary.main}20` : 'none',
                  borderRadius: 1,
                  mx: 1,
                  mb: 0.5,
                  minHeight: '32px',
                  py: 0.5,
                  transition: 'all 0.3s ease'
                }}
              >
                <ListItemIcon sx={{ 
                  color: (item.path && isActiveRoute(item.path)) ? 'primary.main' : 'inherit',
                  minWidth: 40
                }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.title}
                  primaryTypographyProps={{
                    fontSize: '0.875rem',
                    fontWeight: 400
                  }}
                />
                {item.badge && (
                  <Badge badgeContent={item.badge} color="error" />
                )}
                {item.children && (
                  expandedItems[item.title] ? <ExpandLess /> : <ExpandMore />
                )}
              </ListItemButton>
            </ListItem>
            
            {item.children && (
              <Collapse in={expandedItems[item.title]} timeout="auto" unmountOnExit>
                <List 
                  component="div" 
                  disablePadding
                  sx={{
                    backgroundColor: `${theme.palette.primary.main}05`,
                    borderRadius: 1,
                    mx: 1,
                    mt: 0.5,
                    border: `1px solid ${theme.palette.primary.main}10`,
                    transition: 'all 0.3s ease'
                  }}
                >
                  {item.children.map((child) => (
                    <ListItem key={child.title} disablePadding>
                      <ListItemButton
                        onClick={() => child.path && handleNavigation(child.path)}
                        selected={child.path ? isActiveRoute(child.path) : false}
                        sx={{
                          '&.Mui-selected': {
                            backgroundColor: theme.palette.primary.dark,
                            color: 'white',
                            '& .MuiListItemIcon-root': {
                              color: 'white',
                            },
                            '& .MuiListItemText-primary': {
                              color: 'white',
                            },
                            '&:hover': {
                              backgroundColor: theme.palette.primary.dark,
                            },
                          },
                          borderRadius: 1,
                          mx: 1,
                          mb: 0.5,
                          minHeight: '32px',
                          py: 0.5,
                          pl: 3
                        }}
                      >
                        <ListItemIcon sx={{ 
                          color: (child.path && isActiveRoute(child.path)) ? 'primary.main' : 'inherit',
                          minWidth: 40
                        }}>
                          {child.icon}
                        </ListItemIcon>
                        <ListItemText 
                          primary={child.title}
                          primaryTypographyProps={{
                            fontSize: '0.8rem',
                            fontWeight: 400
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            )}
          </React.Fragment>
        ))}
      </List>
    </Box>
  );

  // =====================================================
  // RENDERIZAÇÃO PRINCIPAL
  // =====================================================
  return (
    <Box sx={{ display: 'flex', height: '100vh' }}>
      {/* AppBar */}
      <AppBar 
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${theme.spacing(30)})` },
          ml: { md: `${theme.spacing(30)}` },
          zIndex: theme.zIndex.drawer + 1,
          backgroundColor: 'white',
          color: 'text.primary',
          boxShadow: '0 1px 3px rgba(0,0,0,0.12)'
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          
          <Typography 
            variant="h6" 
            component="div" 
            sx={{ 
              flexGrow: 1,
              display: { xs: 'none', md: 'block' } // Oculta o título no mobile
            }}
          >
            {filteredMenuItems.find(item => item.path && isActiveRoute(item.path))?.title || 'Trajetus'}
          </Typography>

          {/* Notificações */}
          <Tooltip title="Notificações">
            <IconButton 
              color="inherit" 
              sx={{ mr: { xs: 0.5, md: 1 }, transform: { xs: 'none', md: 'translateX(-50px)' } }}
              onClick={handleNotificacoesClick}
            >
              <Badge badgeContent={notificacoesNaoLidas} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>
          </Tooltip>

          {/* Menu dropdown de notificações */}
          <Menu
            anchorEl={notificacoesAnchorEl}
            open={Boolean(notificacoesAnchorEl)}
            onClose={() => setNotificacoesAnchorEl(null)}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            PaperProps={{
              sx: { width: 400, maxHeight: 700 }
            }}
          >
            <Box sx={{ p: 2 }}>
              <Typography variant="h6" component="div">
                Notificações ({notificacoes.length})
              </Typography>
            </Box>
            <Divider />
            
            {notificacoes.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Nenhuma notificação não lida
                </Typography>
              </Box>
            ) : (
              notificacoes.map((notificacao) => (
                <Box key={notificacao.id}>
                  <Box sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                        {new Date(notificacao.data_cadastro).toLocaleDateString('pt-BR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </Typography>
                      <Button 
                        size="small" 
                        variant="outlined" 
                        color="primary"
                        onClick={() => marcarComoLida(notificacao.id)}
                        sx={{ fontSize: '0.7rem', py: 0.5, px: 1 }}
                      >
                        Marcar como Lida
                      </Button>
                    </Box>
                    <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                      De: {notificacao.remetente_nome || 'Sistema'}
                    </Typography>
                    <Typography variant="body2" component="div">
                      {notificacao.mensagem}
                    </Typography>
                  </Box>
                  <Divider />
                </Box>
              ))
            )}
            
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between' }}>
              <Button 
                variant="text" 
                color="primary"
                onClick={() => {
                  setNotificacoesAnchorEl(null);
                  handleNavigation('/app/configuracoes?somenteMinhas=1');
                }}
              >
                Ver todas
              </Button>
              {notificacoes.length > 0 && (
                <Button 
                  variant="contained" 
                  color="primary"
                  onClick={marcarTodasComoLidas}
                >
                  Marcar todas como lidas
                </Button>
              )}
            </Box>
          </Menu>

          {/* Menu do usuário */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, md: 2 }, ml: { xs: '150px', md: 0 } }}>
            {isMobile ? (
              // No mobile: mostrar apenas o primeiro nome, mais compacto
              <Box sx={{ mr: 1, textAlign: { xs: 'left', md: 'right' }, mt: { xs: 0, md: 1 } }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: 500, 
                    lineHeight: 1, 
                    fontSize: { xs: '0.85rem', md: '1rem' },
                    mt: { xs: 0, md: 1 }
                  }}
                >
                  {(user?.nome || user?.user_name || 'Usuário').split(' ')[0]}
                </Typography>
              </Box>
            ) : (
              // No desktop: manter nome completo e email
              <Box sx={{ mr: 2, textAlign: 'right', transform: 'translateY(10px)' }}>
                <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.2, transform: 'translateY(-8px)' }}>
                  {(user?.nome || user?.user_name || 'Usuário')}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ transform: 'translateY(-6px)', display: 'block', lineHeight: 1 }}>
                  {user?.email || 'email@exemplo.com'}
                </Typography>
              </Box>
            )}
            
            <IconButton
              onClick={handleMenuOpen}
              sx={{ p: { xs: 0.25, md: 0 }, flexShrink: 0 }}
            >
              <Avatar 
                src={resolveImageSrc(user?.foto)}
                sx={{ 
                  bgcolor: theme.palette.primary.main,
                  width: { xs: 32, md: 40 },
                  height: { xs: 32, md: 40 },
                  flexShrink: 0
                }}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  console.log(' Erro ao carregar foto do usuário:', {
                    temFoto: !!user?.foto,
                    tamanhoFoto: user?.foto?.length,
                    inicioFoto: user?.foto?.substring(0, 50),
                    src: target.src
                  });
                }}
              >
                {!user?.foto && user?.user_name ? 
                  user.user_name.split(' ')[0].charAt(0).toUpperCase() : 
                  <AccountCircleIcon />
                }
              </Avatar>
            </IconButton>
          </Box>

          {/* Menu dropdown */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <MenuItem onClick={() => {
              handleMenuClose();
              setPerfilModalOpen(true);
            }}>
              <ListItemIcon>
                <AccountCircleIcon fontSize="small" />
              </ListItemIcon>
              Perfil
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <ExitToAppIcon fontSize="small" />
              </ListItemIcon>
              Sair
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Drawer */}
      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={drawerOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: theme.spacing(30),
            backgroundColor: theme.palette.background.default,
            borderRight: `1px solid ${theme.palette.divider}`,
          },
        }}
      >
        {drawer}
      </Drawer>

      {/* Conteúdo principal */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 0,
          width: { md: `calc(100% - ${theme.spacing(30)})` },
          ml: { md: `${theme.spacing(30)}` },
          mt: '64px', // Altura do AppBar
          minHeight: 'calc(100vh - 64px)',
          backgroundColor: theme.palette.grey[50]
        }}
      >
        {children || <Outlet />}
      </Box>

      {/* Modal de perfil */}
      <Dialog
        open={perfilModalOpen}
        onClose={fecharModalPerfil}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { maxHeight: '90vh' }
        }}
      >
        <DialogTitle>Perfil do Usuário</DialogTitle>
        <DialogContent>
          {perfilError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {perfilError}
            </Alert>
          )}
          
          {perfilSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {perfilSuccess}
            </Alert>
          )}

          <Grid container spacing={3} sx={{ mt: 1 }}>
            {/* Foto */}
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', height: '160px', justifyContent: 'space-between' }}>
                <Box sx={{ 
                  width: 100, 
                  height: 100, 
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative'
                }}>
                  <Avatar 
                    src={perfilData.foto ? (
                      perfilData.foto.startsWith('data:') ? perfilData.foto : `data:image/jpeg;base64,${perfilData.foto}`
                    ) : undefined}
                    sx={{ 
                      bgcolor: theme.palette.primary.main,
                      width: 100,
                      height: 100
                    }}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      console.log(' Erro ao carregar foto do usuário:', {
                        temFoto: !!perfilData.foto,
                        tamanhoFoto: perfilData.foto?.length,
                        inicioFoto: perfilData.foto?.substring(0, 50),
                        src: target.src
                      });
                    }}
                  >
                    {!perfilData.foto && user?.user_name ? 
                      user.user_name.split(' ')[0].charAt(0).toUpperCase() : 
                      <AccountCircleIcon />
                    }
                  </Avatar>
                  {perfilData.foto && (
                    <IconButton
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: -8,
                        right: -8,
                        backgroundColor: '#f44336',
                        color: 'white',
                        width: 20,
                        height: 20,
                        '&:hover': {
                          backgroundColor: '#d32f2f'
                        }
                      }}
                      onClick={() => setPerfilData(prev => ({ ...prev, foto: '' }))}
                    >
                      <CancelIcon sx={{ fontSize: 12 }} />
                    </IconButton>
                  )}
                </Box>
                
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button 
                    variant="outlined" 
                    component="label" 
                    startIcon={<CameraAltIcon />}
                    size="small"
                  >
                    Câmera
                    <input 
                      type="file" 
                      hidden 
                      onChange={handleUploadFoto} 
                      accept="image/*"
                      capture="environment"
                    />
                  </Button>
                  <Button 
                    variant="outlined" 
                    component="label" 
                    startIcon={<PhotoLibraryIcon />}
                    size="small"
                  >
                    Galeria
                    <input 
                      type="file" 
                      hidden 
                      onChange={handleUploadFoto} 
                      accept="image/*"
                    />
                  </Button>
                </Box>
              </Box>
            </Grid>

            {/* Assinatura */}
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', height: '160px', justifyContent: 'space-between' }}>
                <Box sx={{ 
                  width: 200, 
                  height: 100, 
                  border: '2px dashed #ccc', 
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#fafafa',
                  position: 'relative'
                }}>
                  {perfilData.assinatura ? (
                    <>
                      <img 
                        src={typeof perfilData.assinatura === 'string' && perfilData.assinatura.startsWith('data:') ? perfilData.assinatura : `data:image/jpeg;base64,${perfilData.assinatura}`} 
                        alt="Assinatura" 
                        style={{ 
                          maxWidth: '100%', 
                          maxHeight: '100%', 
                          objectFit: 'contain'
                        }}
                      />
                      <IconButton
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: -8,
                          right: -8,
                          backgroundColor: '#f44336',
                          color: 'white',
                          width: 20,
                          height: 20,
                          '&:hover': {
                            backgroundColor: '#d32f2f'
                          }
                        }}
                        onClick={() => setPerfilData(prev => ({ ...prev, assinatura: '' }))}
                      >
                        <CancelIcon sx={{ fontSize: 12 }} />
                      </IconButton>
                    </>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      Área da Assinatura
                    </Typography>
                  )}
                </Box>
                
                <Box>
                  <Button 
                    variant="outlined" 
                    component="label" 
                    startIcon={<CloudUploadIcon />}
                  >
                    Assinatura
                    <input 
                      type="file" 
                      hidden 
                      onChange={handleUploadAssinatura} 
                      accept="image/*"
                    />
                  </Button>
                </Box>
              </Box>
            </Grid>

            {/* Nome Completo */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nome Completo"
                value={perfilData.nome}
                onChange={(e) => handlePerfilInputChange('nome', e.target.value)}
                required
              />
            </Grid>

            {/* Nova Senha */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nova Senha (deixe em branco para manter)"
                type={showPassword ? 'text' : 'password'}
                value={perfilData.novaSenha}
                onChange={(e) => handlePerfilInputChange('novaSenha', e.target.value)}
                placeholder="Digite a nova senha"
                error={perfilFieldErrors.novaSenha}
                helperText={
                  perfilFieldErrors.novaSenha 
                    ? "Mínimo 8 dígitos (ao menos 1 letra, 1 número, 1 caracter especial)" 
                    : "Mínimo 8 dígitos (ao menos 1 letra, 1 número, 1 caracter especial)"
                }
                FormHelperTextProps={{
                  sx: { 
                    fontSize: '0.75rem',
                    color: perfilFieldErrors.novaSenha ? 'error.main' : 'text.secondary'
                  }
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setShowPassword(prev => !prev)}
                      >
                        {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Data de Nascimento */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Data de Nascimento"
                type="date"
                value={perfilData.dataNascimento}
                onChange={(e) => handlePerfilInputChange('dataNascimento', e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>

            {/* Celular */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Celular"
                value={perfilData.celular}
                onChange={(e) => handlePerfilInputChange('celular', e.target.value)}
                placeholder="(00) 00000-0000"
              />
            </Grid>

            {/* Email */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={perfilData.email}
                onChange={(e) => handlePerfilInputChange('email', e.target.value)}
                required
              />
            </Grid>

            {/* CRP */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="CRP"
                value={perfilData.crp}
                onChange={(e) => handlePerfilInputChange('crp', e.target.value)}
                placeholder="CRP 00/00000"
              />
            </Grid>

            {/* CPF */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="CPF"
                value={perfilData.cpf}
                onChange={(e) => handlePerfilInputChange('cpf', e.target.value)}
                placeholder="000.000.000-00"
              />
            </Grid>

            {/* RG */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="RG"
                value={perfilData.rg}
                onChange={(e) => handlePerfilInputChange('rg', e.target.value)}
                placeholder="00.000.000-0"
              />
            </Grid>

            {/* Separação - Endereço Completo */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }}>
                <Typography variant="h6" color="primary">
                  Endereço Completo
                </Typography>
              </Divider>
            </Grid>

            {/* CEP */}
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="CEP"
                value={perfilData.cep}
                onChange={(e) => handlePerfilInputChange('cep', e.target.value)}
                placeholder="00000-000"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={buscarEnderecoPorCEP}
                        disabled={!perfilData.cep}
                      >
                        <SearchIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Endereço */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Endereço"
                value={perfilData.endereco}
                onChange={(e) => handlePerfilInputChange('endereco', e.target.value)}
              />
            </Grid>

            {/* Número */}
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Número"
                value={perfilData.numero}
                onChange={(e) => handlePerfilInputChange('numero', e.target.value)}
              />
            </Grid>

            {/* Bairro */}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Bairro"
                value={perfilData.bairro}
                onChange={(e) => handlePerfilInputChange('bairro', e.target.value)}
              />
            </Grid>

            {/* Cidade */}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Cidade"
                value={perfilData.cidade}
                onChange={(e) => handlePerfilInputChange('cidade', e.target.value)}
              />
            </Grid>

            {/* UF */}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="UF"
                value={perfilData.uf}
                onChange={(e) => handlePerfilInputChange('uf', e.target.value)}
                placeholder="SP"
                inputProps={{ maxLength: 2 }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={fecharModalPerfil} 
            color="secondary"
            startIcon={<CancelIcon />}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleSalvarPerfil} 
            color="primary" 
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={Boolean(perfilData.novaSenha && perfilFieldErrors.novaSenha)}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Layout;
